import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type SessionUser = {
  id: string
  email: string
  name: string
  isSuperadmin: boolean
  uiLang: string
  avatarUrl?: string | null
  /*
   * La personne morale dont cette personne relève — le studio pour un
   * superadmin, l'exploitant de ses lieux sinon. `null` quand la réponse est
   * ambiguë, et la ligne ne s'affiche alors pas : afficher la première venue
   * serait affirmer une appartenance qu'on ne connaît pas.
   */
  company?: string | null
}

/**
 * Les capacités, telles que le serveur les a résolues.
 *
 * Le type est large — `string` et non une union littérale — délibérément. La
 * liste vit dans `drizzle/schema.ts`, côté serveur, et la dupliquer ici en
 * union créerait deux sources pour une même vérité : ajouter une capacité
 * demanderait deux commits, et en oublier un ferait échouer la compilation
 * d'un écran sans que rien n'indique où est la liste réelle.
 *
 * La sécurité ne repose pas dessus : chaque route revérifie. Ceci sert à
 * décider ce qu'on AFFICHE.
 */
export type Capability = string

export type VenueSummary = {
  id: string
  slug: string
  name: string
  timezone: string
  currency: string
  /*
   * L'EXPLOITANT du lieu.
   *
   * « Studio 1 » ne dit pas chez qui l'on travaille, et deux exploitants
   * peuvent très bien avoir chacun leur « Studio 1 ». C'est le nom de
   * l'entreprise qui situe.
   */
  operatorId: string
  operatorName: string
  /*
   * LE BILLET EST-IL EXIGÉ DANS CE LIEU ?
   *
   * Facultatif à la lecture : une instance plus ancienne que ce champ ne le
   * renvoie pas, et elle exige forcément un billet — c'était le seul
   * comportement. `!== false` plutôt que `=== true` partout où on le lit.
   */
  ticketRequired?: boolean
  role: string
  /*
   * LES RÉGLAGES DU LIEU dont l'écran a besoin.
   *
   * Facultatif à la lecture : une instance plus ancienne que ce champ ne le
   * renvoie pas, et l'écran retombe alors sur ses défauts.
   */
  settings?: { temperatureUnit?: 'c' | 'f' }
  /** Ce que l'utilisateur peut faire DANS cette venue. */
  can: Capability[]
  /*
   * LES MODULES de l'exploitant, en plus du socle (28/10) : `billetterie`,
   * `marketing`, `equipe`. Un module absent est caché. Facultatif à la
   * lecture : une instance antérieure ne le renvoie pas, et elle montrait
   * tout — absent veut donc dire « tous ».
   */
  modules?: string[]
}

export type Module = 'billetterie' | 'marketing' | 'equipe'

/** Le module est-il actif dans ce lieu ? */
export function venueHasModule(venue: Pick<VenueSummary, 'modules'> | undefined, module: Module): boolean {
  return venue?.modules ? venue.modules.includes(module) : true
}

type AuthState = {
  token: string | null
  user: SessionUser | null
  venues: VenueSummary[]
  /** La venue active. Voyage dans `x-venue-id` sur chaque requête. */
  venueId: string | null
  /**
   * Le preset que le studio a demandé à SIMULER, s'il l'a fait.
   *
   * Il ne sert qu'à poser `x-view-as` : les capacités qui en découlent sont
   * calculées par le serveur et reviennent dans `venues[].can`, comme pour
   * tout le monde. Les dériver ici du nom du preset créerait une seconde
   * source de vérité, et l'interface finirait par montrer autre chose que ce
   * que l'API applique.
   */
  viewAs: string | null
  /**
   * Ce que cette INSTANCE est : le nuage, ou une salle.
   *
   * Il arrive avec la session et n'est jamais deviné. Il ne sert qu'à filtrer
   * la navigation : la vraie frontière est le montage des routeurs côté
   * serveur, et un client qui ignorerait ce champ verrait des écrans dont
   * toutes les requêtes rendent 404 — jamais des données d'un autre mode.
   *
   * Non persisté : il appartient au serveur qu'on interroge, pas au
   * navigateur. Le garder ferait qu'un poste ayant ouvert une salle
   * continuerait de croire qu'il en est une en rouvrant le nuage.
   */
  mode: 'cloud' | 'room'
  /**
   * POURQUOI la session s'est terminée, quand ce n'est pas un simple départ.
   *
   * `session_revoked` veut dire que le mot de passe du compte a changé
   * ailleurs — un autre appareil, une réinitialisation — et que tous les
   * jetons antérieurs sont morts. L'écran de connexion le dit, sinon une
   * sécurité qui fonctionne se lit comme une panne.
   *
   * Volontairement HORS du stockage persistant : c'est un fait de la session
   * qui vient de finir, pas un état à retrouver au prochain démarrage.
   */
  signedOutBecause: 'session_revoked' | null
  signIn: (token: string, user: SessionUser) => void
  /** Remplace le profil après une modification du compte. */
  setUser: (user: SessionUser) => void
  setVenues: (venues: VenueSummary[]) => void
  setVenue: (venueId: string) => void
  setViewAs: (preset: string | null) => void
  signOut: (because?: 'session_revoked' | null) => void
  currentVenue: () => VenueSummary | null
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      venues: [],
      venueId: null,
      viewAs: null,
      mode: 'cloud',
      signedOutBecause: null,

      signIn: (token, user) => set({ token, user, signedOutBecause: null }),

      setVenues: (venues) =>
        set((state) => ({
          venues,
          // Une seule venue accessible : la choisir est une décision sans
          // enjeu, et la demander serait un écran de plus pour rien.
          venueId:
            state.venueId && venues.some((v) => v.id === state.venueId)
              ? state.venueId
              : (venues[0]?.id ?? null),
        })),

      setVenue: (venueId) => set({ venueId }),

      setViewAs: (preset) => set({ viewAs: preset }),

      setUser: (user) => set({ user }),
      signOut: (because = null) =>
        set({
          token: null,
          user: null,
          venues: [],
          venueId: null,
          viewAs: null,
          signedOutBecause: because,
        }),

      currentVenue: () => {
        const { venues, venueId } = get()
        return venues.find((v) => v.id === venueId) ?? null
      },
    }),

    {
      name: 'portage.auth',
      // On ne persiste pas la liste des venues : elle est rechargée à chaque
      // démarrage, et une liste périmée en stockage local ferait apparaître un
      // lieu auquel l'accès a été retiré.
      /* `viewAs` est persisté : un rechargement de page au milieu d'une
         vérification ne doit pas rendre discrètement tous les droits. C'est
         un mode dont on sort par un geste, pas par un F5. */
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        venueId: state.venueId,
        viewAs: state.viewAs,
      }),
    },
  ),
)

/**
 * Le hook de capacité : « cette personne peut-elle faire ça, ici ? »
 *
 * Il remplace les `venue?.role === 'owner' || venue?.role === 'manager'`
 * éparpillés dans neuf fichiers. Ce motif avait deux défauts : il énumérait
 * les rôles AUTORISÉS plutôt que le geste concerné — donc ajouter un preset
 * demandait de retrouver les neuf endroits — et il ignorait complètement les
 * écarts assumés, puisqu'un membre à capacités explicites peut porter le nom
 * de preset `guide` et gérer les ressources.
 *
 * Nommer le geste rend aussi le code lisible : `can('finance:read')` dit ce
 * qu'on protège, `role === 'owner'` dit seulement qui on laisse entrer.
 */
export function useCan(capability: Capability): boolean {
  return useAuth((state) => {
    const venue = state.venues.find((v) => v.id === state.venueId)
    return venue?.can.includes(capability) ?? false
  })
}

/** Le module est-il actif dans le lieu courant ? */
export function useModule(module: Module): boolean {
  return useAuth((state) => venueHasModule(state.venues.find((v) => v.id === state.venueId), module))
}

/** La variante impérative, pour du code hors composant. */
export function can(capability: Capability): boolean {
  const { venues, venueId } = useAuth.getState()
  return venues.find((v) => v.id === venueId)?.can.includes(capability) ?? false
}

/*
 * LE NOM DU COMPOSANT, dans la suite Portage OS.
 *
 * « Portage » seul ne désigne plus rien depuis que le produit se déploie en
 * deux composants : le nuage qui vend et garde le fichier client, la salle qui
 * pilote la soirée. Les deux servent le MÊME `dist/` — `server/index.ts` monte
 * la même coque dans les deux modes — donc un nom écrit en dur afficherait
 * « Portage Cloud » sur la machine posée dans le lieu, c'est-à-dire exactement
 * la confusion que nommer les composants doit éviter.
 *
 * Ce sont des noms propres : ils ne passent pas par l'i18n, parce qu'ils ne se
 * traduisent pas. Les mettre dans `strings.ts` obligerait à les tenir en
 * double sans qu'aucune des deux copies puisse jamais différer.
 */
export const PRODUCT_NAMES = { cloud: 'Portage Cloud', room: 'Portage Room' } as const

/**
 * Le nom à afficher ici et maintenant.
 *
 * Avant la session, `mode` vaut « cloud » — sa valeur par défaut, et le store
 * refuse délibérément de la persister (voir le champ). Un écran servi avant
 * l'authentification ne doit donc pas s'en servir : il affiche le nom de la
 * SUITE, qui est vrai dans les deux modes.
 */
export function useProductName(): string {
  return PRODUCT_NAMES[useAuth((state) => state.mode)]
}
